import _ from 'lodash'
import { buildWidgetUrl } from './resolveRemoteRefs'
import {
	pointers,
	Component,
	Structure,
	Data,
	QueryProperty,
	ActionBehavior,
	ObsoleteBehaviorsList,
	ViewMode,
	BaseDataItem,
} from '@wix/thunderbolt-becky-types'
import { InflatedByRefStructure, RefData, ResolvedRef } from '../types/byRef'
import { DATA_QUERIES_TO_INFLATE, getUniqueId, REF_DELIMITER, removeHash } from './utils'
import { inflateRefData } from './refDataInflation'
import { getPresetsArray } from './presetsUtils'

const DATA_MAPS = pointers.data.DATA_MAPS
const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES
const QUERY_TO_MAP_NAME = pointers.QUERY_TO_MAP_NAME
const isMobile = (viewMode: ViewMode) => viewMode === 'mobile'

const resolveInternalRefs = (rootCompId: string, masterPageStructure: Structure, masterPageData: Data): ResolvedRef => {
	const structureResult: InflatedByRefStructure = {}

	// TODO: fix definition for data
	const dataResult: RefData = {
		[DATA_MAPS.BEHAVIORS]: {},
		[DATA_MAPS.BREAKPOINTS]: {},
		[DATA_MAPS.PROPERTIES]: {},
		[DATA_MAPS.CONNECTIONS]: {},
		[DATA_MAPS.DESIGN]: {},
		[DATA_MAPS.DATA]: {},
		[DATA_MAPS.LAYOUT]: {},
		[DATA_MAPS.MOBILE_HINTS]: {},
		[DATA_MAPS.STYLE]: {},
		[DATA_MAPS.TRANSFORMATIONS]: {},
		[DATA_MAPS.TRANSITIONS]: {},
		[DATA_MAPS.ANCHOR]: {},
		[DATA_MAPS.VARIANTS]: {},
		[DATA_MAPS.PRESETS]: {},
	}

	const resolveAndAddComponentFromStructure = (component: Component) =>
		(structureResult[component.id] = { ...component, templateId: component.id })

	const resolveAndAddComponentDataItems = (component: Component) => {
		Object.keys(QUERY_TO_MAP_NAME).forEach((dataQuery) => {
			const compQuery = component[dataQuery as QueryProperty]
			if (!compQuery) {
				return
			}

			const dataMap = QUERY_TO_MAP_NAME[dataQuery as QueryProperty]
			dataResult[dataMap][compQuery] = {
				...masterPageData[dataMap][compQuery],
			}
		})
	}

	const resolveStructureAndDataRecursively = (component: Component) => {
		const childComponent = resolveAndAddComponentFromStructure(component)
		resolveAndAddComponentDataItems(childComponent)

		if (!component.components || component.components.length === 0) {
			return
		}
		component.components.forEach((childComponentId: string) => {
			const childComp = masterPageStructure[childComponentId]
			resolveStructureAndDataRecursively(childComp)
		})
	}

	const rootComp = masterPageStructure[rootCompId]
	resolveStructureAndDataRecursively(rootComp)

	return { structure: structureResult, data: dataResult, rootId: rootCompId }
}

const getUniqueDataQuery = (id: string, suffix: string, withMobileSuffix?: boolean) =>
	`${id}-${suffix}${withMobileSuffix ? '-mobile' : ''}`

const inflateDataQueries = (compStructure: Component, compUniqueId: string, pageData: Data, viewMode: ViewMode) => {
	const flatData = Object.assign({}, ...Object.values(pageData))

	return _(DATA_QUERIES_TO_INFLATE)
		.mapKeys((x) => x)
		.mapValues((dataQueryName) => {
			const originalData = compStructure[dataQueryName]
			const isMobileProperty = isMobile(viewMode) && dataQueryName === PROPERTY_TYPES.PROPERTY_QUERY
			const uniqueDataQuery = getUniqueDataQuery(compUniqueId, dataQueryName, isMobileProperty)
			const dataOverride = flatData[uniqueDataQuery]
			return originalData || dataOverride ? uniqueDataQuery : null
		})
		.pickBy((x) => x)
		.value()
}

const inflateSingleComp = (
	compStructure: Component,
	originalCompId: string,
	refId: string,
	rootId: string,
	pageData: Data,
	viewMode: ViewMode,
	resolvedData: Data | RefData
) => {
	const compUniqueId = getUniqueId(refId, originalCompId)
	const isRootComp = originalCompId === rootId

	const inflatedPropQuery = getUniqueDataQuery(compUniqueId, PROPERTY_TYPES.PROPERTY_QUERY, isMobile(viewMode))

	const originalPropQuery = compStructure[PROPERTY_TYPES.PROPERTY_QUERY]
		? compStructure[PROPERTY_TYPES.PROPERTY_QUERY]
		: ''

	const propertyItemOverrides: Partial<{ ghost?: string } & BaseDataItem<string>> = pageData[DATA_MAPS.PROPERTIES][
		inflatedPropQuery
	]
		? pageData[DATA_MAPS.PROPERTIES][inflatedPropQuery]
		: {}

	let originalPropertyItem: Partial<{ ghost?: string } & BaseDataItem<string>> = {}
	if (originalPropQuery) {
		originalPropertyItem = resolvedData[DATA_MAPS.PROPERTIES][originalPropQuery]
			? resolvedData[DATA_MAPS.PROPERTIES][originalPropQuery]
			: {}
	}

	const propertyItemToUse = _.isEmpty(propertyItemOverrides) ? originalPropertyItem : propertyItemOverrides

	const isGhostCollapsed = propertyItemToUse.ghost === 'COLLAPSED'

	const inflatedSingleComp = {
		...compStructure,
		...inflateDataQueries(compStructure, compUniqueId, pageData, viewMode),
		templateId: originalCompId,
		id: compUniqueId,
		parent: isRootComp ? refId : getUniqueId(refId, compStructure.parent as string),
		components: compStructure.components
			? compStructure.components.map((childCompId) => getUniqueId(refId, childCompId))
			: undefined,
	}
	return { inflatedSingleComp, isGhostCollapsed }
}

const inflateRef = (
	resolvedStructureAndData: ResolvedRef,
	refId: string,
	pageStructure: Structure,
	pageData: Data,
	masterPageStructure: Structure,
	masterPageData: Data,
	pageCompId: string,
	viewMode: ViewMode,
	resolveGhosts: boolean
): ResolvedRef => {
	const { rootId, structure: resolvedStructure, data: resolvedData } = resolvedStructureAndData

	const getRemoteAndMasterPageStructure = _.once(() => ({ ...masterPageStructure, ...resolvedStructure }))

	const inflatedStructure: InflatedByRefStructure = _(resolvedStructure)
		.mapValues((comp, originalCompId) => {
			const { inflatedSingleComp, isGhostCollapsed } = inflateSingleComp(
				comp,
				originalCompId,
				refId,
				rootId,
				pageData,
				viewMode,
				resolvedData
			)

			if (isGhostCollapsed && !resolveGhosts) {
				return {
					...inflatedSingleComp,
					components: [],
					componentType: 'GhostRefComp',
					connectionQuery: null,
				}
			} else {
				// keep potential ghost component in the structure so data will be evaluated for it
				return inflatedSingleComp
			}
		})
		.mapKeys('id')
		.value()

	const presetsQuery = removeHash(pageStructure[refId].presetsQuery || '')
	const presetsDataMap = pageData.presets || {}
	const presets = getPresetsArray(presetsQuery, presetsDataMap)

	const inflatedData: RefData = inflateRefData(
		inflatedStructure,
		resolvedStructureAndData,
		masterPageData,
		refId,
		pageCompId,
		getRemoteAndMasterPageStructure,
		presets
	)

	return { structure: inflatedStructure, data: inflatedData, rootId: getUniqueId(refId, rootId) }
}

const mergeData = (
	original: Data,
	resolvedRefMapToMerge: { [key: string]: ResolvedRef },
	remoteRefMapToMerge: { [key: string]: any }
) => {
	const { connections_data: originalConnectionData, ...originalWithoutConnections } = original
	const originalConnectionsWithoutCopied = { ...originalConnectionData }

	const resolvedRefMapper = (resolvedRef: ResolvedRef) => {
		const data = resolvedRef?.data ?? {}
		const { connections_data: resolvedConnectionData } = data

		if (!resolvedConnectionData) {
			return data
		}

		const fixedConnectionsDataEntries = Object.entries(resolvedConnectionData).map(([id, connectionItem]) => {
			if (!connectionItem?.items || !originalConnectionData[id]?.items) {
				return [id, connectionItem]
			}

			delete originalConnectionsWithoutCopied[id]

			return [
				id,
				{
					...connectionItem,
					// Combine the two item lists instead of overriding with _.merge to avoid strange overrides
					// https://github.com/wix-private/bolt/blob/master/santa-renderer/src/aspects/ComponentsModelAspect/carmi/utils/templateRefsUtils.carmi.js#L35
					items: [...connectionItem.items, ...originalConnectionData[id]?.items],
				},
			]
		})

		return {
			...data,
			connections_data: Object.fromEntries(fixedConnectionsDataEntries),
		}
	}

	return _.merge(
		{},
		...Object.values(resolvedRefMapToMerge).map(resolvedRefMapper),
		...Object.values(remoteRefMapToMerge).map(resolvedRefMapper),
		originalWithoutConnections,
		{ connections_data: originalConnectionsWithoutCopied }
	)
}

const fixDataAfterInflate = (pageData: Data, originalCompIdToInflatedCompIdMap: { [key: string]: string }) => {
	const fixBehaviourItem = (behaviourItem: ActionBehavior) => {
		if (behaviourItem.action && originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]) {
			behaviourItem.action.sourceId = originalCompIdToInflatedCompIdMap[behaviourItem.action.sourceId]
		}

		if (behaviourItem.behavior && originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]) {
			behaviourItem.behavior.targetId = originalCompIdToInflatedCompIdMap[behaviourItem.behavior.targetId]
		}

		return behaviourItem
	}

	Object.keys(pageData.behaviors_data).forEach((behaviourId) => {
		const dataItem = pageData.behaviors_data[behaviourId] as ObsoleteBehaviorsList

		if (dataItem.type === 'ObsoleteBehaviorsList') {
			const itemsString = (dataItem as ObsoleteBehaviorsList).items
			try {
				const itemsList = JSON.parse(itemsString) as Array<ActionBehavior>
				dataItem.items = JSON.stringify(itemsList.map(fixBehaviourItem))
				pageData.behaviors_data[behaviourId] = dataItem
			} catch (e) {
				console.error('Cannot stringify ObsoleteBehaviorsList', e)
			}
		}
	})

	return pageData
}
export {
	resolveInternalRefs,
	inflateRef,
	getUniqueId,
	getUniqueDataQuery,
	mergeData,
	fixDataAfterInflate,
	REF_DELIMITER,
	buildWidgetUrl,
}
